/**
 * Calibri et Cambria pour le canvas, sur un poste qui ne les a pas.
 *
 * Le canvas peint avec les polices installées sur le poste. Sur un Mac, Calibri
 * et Cambria n'y sont pas : Office les garde dans son propre paquet, invisibles
 * de Safari et de Chrome, même Word installé. Un acte écrit en Calibri (police
 * de la mise en page du cabinet) s'y affichait donc dans la police de repli du
 * navigateur, et l'avocate concluait que sa police n'était pas appliquée alors
 * que le .docx la portait bien (Mac Safari, 2026-10-27 ; sous Windows, où
 * Calibri est installée, le même réglage s'affichait juste).
 *
 * Chaque face déclare la famille Office elle-même : `local()` prend la vraie
 * police quand le poste l'a, sinon le navigateur télécharge Carlito ou Caladea,
 * libres (OFL 1.1) et aux mêmes chasses, ce qui garde les coupures de ligne et
 * de page de Word. Seul écart connu : l'accent grave isolé (U+0060) de Caladea
 * en gras italique (relevé de SuperDoc, `substitution-evidence.d.ts`). Le .docx
 * n'en sait rien : il garde « Calibri ».
 *
 * SuperDoc 2.15.1 mesure le texte une fois au montage, sans réécouter le
 * chargement des polices : les faces doivent être chargées avant qu'il monte,
 * d'où leur chargement par `chargerLEditeurDeRedaction`. Ce chargement est borné
 * (`BORNE_DE_CHARGEMENT_MS`) : une police qui ne vient pas ne doit pas retenir
 * le canvas, dont l'admission d'une rédaction n'attend que 26 s.
 */

import caladea400Italic from '@fontsource/caladea/files/caladea-latin-400-italic.woff2?url';
import caladea400 from '@fontsource/caladea/files/caladea-latin-400-normal.woff2?url';
import caladea700Italic from '@fontsource/caladea/files/caladea-latin-700-italic.woff2?url';
import caladea700 from '@fontsource/caladea/files/caladea-latin-700-normal.woff2?url';
import carlito400Italic from '@fontsource/carlito/files/carlito-latin-400-italic.woff2?url';
import carlito400 from '@fontsource/carlito/files/carlito-latin-400-normal.woff2?url';
import carlito700Italic from '@fontsource/carlito/files/carlito-latin-700-italic.woff2?url';
import carlito700 from '@fontsource/carlito/files/carlito-latin-700-normal.woff2?url';

interface FaceDeSubstitution {
  readonly famille: 'Calibri' | 'Cambria';
  readonly poids: '400' | '700';
  readonly style: 'normal' | 'italic';
  /** Noms complet et PostScript de la face Office, que `local()` recherche sur le poste. */
  readonly nomsLocaux: readonly string[];
  readonly url: string;
}

const FACES_DE_SUBSTITUTION: readonly FaceDeSubstitution[] = [
  { famille: 'Calibri', poids: '400', style: 'normal', nomsLocaux: ['Calibri'], url: carlito400 },
  { famille: 'Calibri', poids: '700', style: 'normal', nomsLocaux: ['Calibri Bold', 'Calibri-Bold'], url: carlito700 },
  { famille: 'Calibri', poids: '400', style: 'italic', nomsLocaux: ['Calibri Italic', 'Calibri-Italic'], url: carlito400Italic },
  { famille: 'Calibri', poids: '700', style: 'italic', nomsLocaux: ['Calibri Bold Italic', 'Calibri-BoldItalic'], url: carlito700Italic },
  { famille: 'Cambria', poids: '400', style: 'normal', nomsLocaux: ['Cambria'], url: caladea400 },
  { famille: 'Cambria', poids: '700', style: 'normal', nomsLocaux: ['Cambria Bold', 'Cambria-Bold'], url: caladea700 },
  { famille: 'Cambria', poids: '400', style: 'italic', nomsLocaux: ['Cambria Italic', 'Cambria-Italic'], url: caladea400Italic },
  { famille: 'Cambria', poids: '700', style: 'italic', nomsLocaux: ['Cambria Bold Italic', 'Cambria-BoldItalic'], url: caladea700Italic },
];

/** La source CSS d'une face : la police du poste d'abord, le substitut ensuite. */
function sourceDeLaFace(face: FaceDeSubstitution): string {
  const locales = face.nomsLocaux.map((nom) => `local("${nom}")`);
  return [...locales, `url("${face.url}") format("woff2")`].join(', ');
}

const BORNE_DE_CHARGEMENT_MS = 3_000;

let chargement: Promise<void> | null = null;

/**
 * Déclare et charge les faces, une fois par page. Ne rejette jamais, et se résout
 * au plus tard après `BORNE_DE_CHARGEMENT_MS` : une face qui ne se charge pas
 * laisse le repli du navigateur, comme avant ce module, et ne doit pas empêcher
 * le canvas de monter.
 */
export function chargerLesPolicesDeSubstitution(): Promise<void> {
  if (!chargement) {
    // Sans l'API de chargement des polices (jsdom des tests de composants), il n'y a rien à déclarer.
    if (typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts) {
      chargement = Promise.resolve();
      return chargement;
    }
    const toutesChargees = Promise.all(
      FACES_DE_SUBSTITUTION.map((face) => {
        const fontFace = new FontFace(face.famille, sourceDeLaFace(face), { weight: face.poids, style: face.style });
        document.fonts.add(fontFace);
        return fontFace.load().catch((erreur: unknown) => {
          console.warn(`[canvas] police de substitution non chargée : ${face.famille} ${face.poids} ${face.style} (${face.url})`, erreur);
        });
      }),
    ).then(() => undefined);
    let minuteur: ReturnType<typeof setTimeout> | undefined;
    const borne = new Promise<void>((resolve) => {
      minuteur = setTimeout(() => {
        console.warn(`[canvas] polices de substitution non chargées après ${BORNE_DE_CHARGEMENT_MS} ms : le canvas monte avec le repli du navigateur`);
        resolve();
      }, BORNE_DE_CHARGEMENT_MS);
    });
    chargement = Promise.race([toutesChargees.then(() => clearTimeout(minuteur)), borne]);
  }
  return chargement;
}
